// @flow

import * as React from 'react';
import styled from 'styled-components';
import type { StyledComponent } from 'styled-components';
import { InputOption } from '../../internal/internal-typography';
import Theme from './theme';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';
import { Grid } from '../../layouts/grid';
import CheckmarkIcon from '../../icons/checkmark';
import CloseIcon from '../../icons/close';

/*
  Adapted from this blog post:
  https://css-tricks.com/custom-styling-form-inputs-with-modern-css-features/

  One commenter converted the sass in the post into react and styled components:
  https://codesandbox.io/s/custom-css-only-form-inputs-react-hofx5?file=/src/Checkbox.tsx
 */

type StandaloneToggleProps = {
  /** prevent interacting with the toggle */
  disabled?: boolean,
  /** Current state */
  checked: boolean,
  /**
   * On change callback
   *
   * @param checked the new state of the toggle (a shortcut for event.currentTarget.checked)
   * @param e the full event. */
  onChange: (checked: boolean, e?: SyntheticEvent<HTMLInputElement>) => void,
};

type ToggleProps = StandaloneToggleProps & {
  /** Every toggle should have an associated label for accessibility */
  label: string,
  value: string,
  ...DataProvidesProps,
};

const Toggle = (props: ToggleProps) => {
  const { disabled, label, onChange, ...rest } = props;

  return (
    <StyledLabel disabled={disabled} {...getDataProvidesProps(props)}>
      <Grid container spacing={2}>
        <Grid item>
          <ToggleContainer>
            <StandaloneToggle
              disabled={disabled}
              onChange={onChange}
              {...rest}
            />
          </ToggleContainer>
        </Grid>
        <Grid item xs>
          {label}
        </Grid>
      </Grid>
    </StyledLabel>
  );
};
Toggle.displayName = 'Toggle';
Toggle.defaultProps = { 'data-provides': 'toggle' };

/**
 * This toggle is not documented in the storybook because it should not be
 * used by consumers directly. Instead it should be used as part of other
 * seedling components that take into account accessibility.
 */
export const StandaloneToggle = (props: StandaloneToggleProps) => {
  const { disabled, onChange, ...rest } = props;

  const wrappingOnChange = onChange
    ? (e) => {
        onChange(e.currentTarget.checked, e);
      }
    : null;

  return (
    <ToggleContainer>
      <StyledCheckmarkIcon />
      <StyledCloseIcon />
      <ToggleInput disabled={disabled} onChange={wrappingOnChange} {...rest} />
    </ToggleContainer>
  );
};
StandaloneToggle.defaultProps = {
  disabled: false,
};
StandaloneToggle.displayName = 'StandaloneToggle';

const ToggleContainer: StyledComponent<*, *, *> = styled.span`
  position: relative;
  font-size: 1rem;
  line-height: 1.5rem;
`;

const ToggleInput: StyledComponent<*, *, *> = styled.input.attrs((props) => ({
  type: 'checkbox',
  ...getDataProvidesProps(props),
}))`
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: ${Theme.default.backgroundColor};
  border-radius: 62.5rem;
  cursor: pointer;
  display: inline-block;

  height: 1.375rem;
  width: 2.75rem;
  margin: 0;

  outline: none;
  position: relative;
  transition: background-color 0.3s, box-shadow 0.2s,
    transform 0.6s cubic-bezier(0.2, 0.85, 0.32, 1.2) 0.3s;

  vertical-align: top;

  &:hover:not(:disabled),
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    filter: drop-shadow(0px 2px 6px ${Theme.active.dropShadowColor});
  }

  // The after element represents the circle switch that slides back and forth on toggle.
  &:after {
    margin: 0;
    padding: 0;
    position: absolute;
    border-radius: 50%;
    content: '';
    display: block;
    height: 1rem;
    width: 1rem;
    left: 0.25rem;
    top: 0.185rem;
    transition: transform 0.3s ease;
    z-index: 1;

    background-color: ${Theme.default.switchColor};
    transform: translateX(0);
  }

  &:checked {
    background-color: ${Theme.checked.backgroundColor};

    &:after {
      transform: translateX(1.25rem);
    }
  }

  &:disabled {
    background-color: ${Theme.disabled.backgroundColor};
    cursor: not-allowed;

    &:after {
      background-color: ${Theme.disabled.switchColor};
    }
  }
`;

const StyledLabel: StyledComponent<
  { disabled: boolean },
  *,
  InputOption
> = styled(InputOption)`
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
  cursor: not-allowed;
  color: ${Theme.disabled.labelColor};
  `};
`;

const AbsoluteIconWrapper = styled.span`
  position: absolute;
  pointer-events: none;
  display: inline-flex;
  z-index: 1;
  top: 0.0625rem;
  height: 1em;
  width: 1em;

  svg {
    height: 1em;
    width: 1em;
  }

  ${({ leftAlign }) => leftAlign && `left: .3rem;`}
  ${({ rightAlign }) => rightAlign && `right: .3rem;`}
`;

const StyledCheckmarkIcon = () => {
  return (
    <AbsoluteIconWrapper leftAlign>
      <CheckmarkIcon color={Theme.iconColor} />
    </AbsoluteIconWrapper>
  );
};

const StyledCloseIcon = () => {
  return (
    <AbsoluteIconWrapper rightAlign>
      <CloseIcon color={Theme.iconColor} />
    </AbsoluteIconWrapper>
  );
};

export default Toggle;
